/* Référentiel des 18 compétences — source unique du Rapport d'impact.
 *
 * Rédigé par Nawal (fiche RETOURS_HICHAM_SACLAY_COMPLE_V3, 03/09/2026), points
 * R.1 et R.3. Trois familles de SIX compétences chacune : la répartition
 * précédente donnait 6 / 7 / 5, ce qui déséquilibrait les pages du rapport.
 *
 * Deux choses cohabitent ici, ne pas les confondre :
 *   - `cle`     : la clé telle qu'elle sort de la notation (`resultFin`). Elle
 *                 vient des contenus et sa CASSE compte (« Flexibilité Mentale »
 *                 avec un M majuscule sur les contenus 95 et 97).
 *   - `libelle` : le nom lisible par un client, souvent plus long que la clé.
 *
 * ⚠️ La famille déclarée ici peut différer de celle inscrite dans le champ
 * `softSkills` des contenus en base : c'est volontaire et c'est CE fichier qui
 * fait foi pour le rapport, afin d'obtenir 6 / 6 / 6 sans écriture en base.
 * Aligner la base reste souhaitable — le radar de la Certification, lui, groupe
 * encore par la famille du contenu.
 */

/* 04/09 — VERSION ANGLAISE. Le rapport partait chez un client anglophone
 * entierement en francais. Chaque intitule porte donc son pendant `...En`.
 * ⚠️ Traduction a faire relire par Nawal : ce sont ses textes, et les nuances
 * du referentiel scientifique comptent. Le francais n'a pas ete touche. */
export const FAMILLES = {
  leadership :    { ordre: 1, libelle: "Leadership & Collaboration", libelleEn: "Leadership & Collaboration" },
  communication : { ordre: 2, libelle: "Innovation & Communication", libelleEn: "Innovation & Communication" },
  efficacite :    { ordre: 3, libelle: "Efficacité opérationnelle",  libelleEn: "Operational effectiveness" },
}

export const REFERENTIEL = [
  /* ---- Leadership & Collaboration (6) ---- */
  {
    cle: "Régulation émotionnelle", famille: "leadership",
    libelle: "Régulation émotionnelle",
    libelleEn: "Emotional regulation",
    mesure: "reconnaître ses émotions et celles d'autrui, ajuster ses réactions et maintenir un comportement approprié pour préserver la qualité des interactions et la performance collective",
    mesureEn: "recognising one's own emotions and those of others, adjusting one's reactions and maintaining appropriate behaviour to preserve the quality of interactions and collective performance",
    pertinence: "gérer les tensions, coopérer sous pression, encadrer une équipe",
    pertinenceEn: "managing tension, cooperating under pressure, leading a team",
  },
  {
    cle: "Empathie", famille: "leadership",
    libelle: "Empathie et identification des signaux non verbaux",
    libelleEn: "Empathy and reading of non-verbal cues",
    mesure: "détecter et interpréter les émotions, besoins et signaux non verbaux d'autrui pour adapter sa communication",
    mesureEn: "detecting and interpreting the emotions, needs and non-verbal cues of others in order to adapt one's communication",
    pertinence: "relation client, management, travail en équipe",
    pertinenceEn: "client relations, management, teamwork",
  },
  {
    cle: "Influence assertive", famille: "leadership",
    libelle: "Influence assertive",
    libelleEn: "Assertive influence",
    mesure: "exprimer clairement ses idées et besoins en respectant ceux des autres, pour convaincre, mobiliser et coopérer",
    mesureEn: "expressing one's ideas and needs clearly while respecting those of others, in order to convince, mobilise and cooperate",
    pertinence: "négociation, conduite de projet, leadership",
    pertinenceEn: "negotiation, project leadership, management",
  },
  {
    cle: "Positivité", famille: "leadership",
    libelle: "Positivité et communication constructive",
    libelleEn: "Positivity and constructive communication",
    mesure: "maintenir une attitude encourageante et orientée solution, valoriser les réussites, formuler des feedbacks qui renforcent la confiance",
    mesureEn: "maintaining an encouraging, solution-oriented attitude, recognising successes, and giving feedback that builds confidence",
    pertinence: "cohésion d'équipe, feedback, climat de travail",
    pertinenceEn: "team cohesion, feedback, working climate",
  },
  {
    cle: "Prise d'initiative", famille: "leadership",
    libelle: "Prise d'initiative",
    libelleEn: "Initiative",
    mesure: "engager spontanément des actions pertinentes, anticiper les besoins, proposer des solutions sans attendre d'instruction",
    mesureEn: "spontaneously taking relevant action, anticipating needs, and proposing solutions without waiting for instructions",
    pertinence: "proactivité, autonomie, innovation",
    pertinenceEn: "proactivity, autonomy, innovation",
  },
  {
    // R.1 : deplacee depuis « Efficacite operationnelle »
    cle: "Flexibilité Mentale", famille: "leadership", court: "Flexibilité mentale",
    libelle: "Flexibilité mentale",
    libelleEn: "Mental flexibility",
    mesure: "réorganiser rapidement ses actions, changer de perspective, adapter ses stratégies face à des tâches ou contraintes nouvelles",
    mesureEn: "quickly reorganising one's actions, shifting perspective, and adapting strategies in the face of new tasks or constraints",
    pertinence: "environnements changeants, priorités multiples",
    pertinenceEn: "changing environments, competing priorities",
  },

  /* ---- Innovation & Communication (6) ---- */
  {
    cle: "Raisonnement dans l'incertitude", famille: "communication",
    libelle: "Tolérance au risque et raisonnement dans l'incertitude",
    libelleEn: "Risk tolerance and reasoning under uncertainty",
    mesure: "analyser et agir efficacement dans des situations ambiguës ou contradictoires, décider malgré un manque d'informations",
    mesureEn: "analysing and acting effectively in ambiguous or contradictory situations, deciding despite missing information",
    pertinence: "décision en contexte incertain, gestion de l'imprévu",
    pertinenceEn: "decision-making under uncertainty, handling the unexpected",
  },
  {
    cle: "Raisonnement divergent", famille: "communication",
    libelle: "Raisonnement divergent",
    libelleEn: "Divergent thinking",
    mesure: "produire des idées nouvelles, explorer plusieurs pistes, imaginer des solutions alternatives",
    mesureEn: "generating new ideas, exploring several avenues, and imagining alternative solutions",
    pertinence: "créativité, résolution de problèmes",
    pertinenceEn: "creativity, problem-solving",
  },
  {
    cle: "Raisonnement logique", famille: "communication",
    libelle: "Raisonnement logique",
    libelleEn: "Logical reasoning",
    mesure: "établir des liens, identifier des régularités et extraire des tendances pour une décision cohérente",
    mesureEn: "establishing connections, identifying regularities and extracting trends to reach a coherent decision",
    pertinence: "analyse, diagnostic, décision",
    pertinenceEn: "analysis, diagnosis, decision-making",
  },
  {
    cle: "Curiosité", famille: "communication",
    libelle: "Curiosité",
    libelleEn: "Curiosity",
    mesure: "rechercher activement de nouvelles informations, explorer, poser des questions pour enrichir sa compréhension",
    mesureEn: "actively seeking new information, exploring, and asking questions to deepen one's understanding",
    pertinence: "apprentissage continu, veille, adaptation",
    pertinenceEn: "continuous learning, keeping watch, adaptation",
  },
  {
    cle: "Raisonnement synthétique", famille: "communication",
    libelle: "Raisonnement synthétique",
    libelleEn: "Synthetic reasoning",
    mesure: "sélectionner, organiser et restituer les informations essentielles de sources multiples",
    mesureEn: "selecting, organising and restituting the essential information drawn from multiple sources",
    pertinence: "reporting, synthèse, aide à la décision",
    pertinenceEn: "reporting, synthesis, decision support",
  },
  {
    // R.1 : deplacee depuis « Leadership »
    cle: "Prise de parole", famille: "communication",
    libelle: "Prise de parole en public",
    libelleEn: "Public speaking",
    mesure: "transmettre clairement et efficacement des informations à un groupe, en adaptant son discours et sa posture à l'audience",
    mesureEn: "conveying information clearly and effectively to a group, adapting one's delivery and posture to the audience",
    pertinence: "présentations, réunions, entretiens",
    pertinenceEn: "presentations, meetings, interviews",
  },

  /* ---- Efficacité opérationnelle (6) ---- */
  {
    cle: "Persévérance", famille: "efficacite",
    libelle: "Persévérance",
    libelleEn: "Perseverance",
    mesure: "maintenir l'effort, surmonter les obstacles, poursuivre un objectif jusqu'à son aboutissement",
    mesureEn: "sustaining effort, overcoming obstacles, and pursuing an objective through to completion",
    pertinence: "projets longs, atteinte des objectifs",
    pertinenceEn: "long projects, meeting objectives",
  },
  {
    cle: "Planification", famille: "efficacite",
    libelle: "Planification",
    libelleEn: "Planning",
    mesure: "organiser les tâches dans le temps, définir des priorités, respecter les échéances",
    mesureEn: "organising tasks over time, setting priorities, and meeting deadlines",
    pertinence: "gestion de projet, délais",
    pertinenceEn: "project management, deadlines",
  },
  {
    cle: "Monitoring", famille: "efficacite",
    libelle: "Monitoring (suivi, évaluation, ajustement)",
    libelleEn: "Monitoring (tracking, evaluating, adjusting)",
    mesure: "suivre sa progression, analyser les écarts, ajuster ses stratégies",
    mesureEn: "tracking one's progress, analysing gaps, and adjusting one's strategies",
    pertinence: "amélioration continue, autonomie, qualité",
    pertinenceEn: "continuous improvement, autonomy, quality",
  },
  {
    cle: "Efficacité", famille: "efficacite",
    libelle: "Efficacité",
    libelleEn: "Effectiveness",
    mesure: "atteindre des résultats élevés en optimisant ressources, temps et énergie",
    mesureEn: "achieving high results while optimising resources, time and energy",
    pertinence: "productivité, performance",
    pertinenceEn: "productivity, performance",
  },
  {
    cle: "Réactivité", famille: "efficacite",
    libelle: "Réactivité",
    libelleEn: "Responsiveness",
    mesure: "répondre rapidement aux imprévus, ajuster ses priorités, adapter son action",
    mesureEn: "responding quickly to the unexpected, adjusting priorities, and adapting one's action",
    pertinence: "gestion de l'urgence, adaptation",
    pertinenceEn: "handling urgency, adaptation",
  },
  {
    cle: "Rigueur", famille: "efficacite",
    libelle: "Rigueur et respect des règles (incluant autonomie opérationnelle)",
    libelleEn: "Rigour and compliance (including operational autonomy)",
    mesure: "appliquer les procédures avec précision et constance, travailler de façon fiable et autonome dans un cadre normé",
    mesureEn: "applying procedures precisely and consistently, working reliably and autonomously within a regulated framework",
    pertinence: "conformité, fiabilité, secteurs réglementés",
    pertinenceEn: "compliance, reliability, regulated sectors",
  },
]

/* Recherche tolérante : la casse et les accents varient d'un contenu à l'autre
   (« Flexibilité Mentale » vs « Flexibilité mentale »), et c'est exactement ce
   genre d'écart qui a fait retomber dix compétences sur une valeur par défaut
   dans le moteur de simulation. On ne veut pas rejouer ça ici. */
const aplatir = (t) => String(t == null ? "" : t)
  .toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/['’\s-]/g, "")

const INDEX = REFERENTIEL.reduce((acc, e) => {
  acc[aplatir(e.cle)] = e
  acc[aplatir(e.libelle)] = e
  return acc
}, {})

export const trouverCompetence = (cle) => INDEX[aplatir(cle)] || null

/* Deux libelles, deux usages — ne pas les intervertir.
 *
 * `libelleAffichable` sert AUX ECRANS : radar de certification, listes du volet
 * lateral. Il reste COURT, sinon il deborde du cadre — essaye le 03/09 avec les
 * libelles longs, « Empathie et identification des signaux non verbaux » sortait
 * du radar et se coupait dans la liste. Il ne fait que corriger la casse
 * technique des contenus (« Flexibilité Mentale » avec un M majuscule).
 *
 * `libelleComplet` sert au RAPPORT PDF, large de 1250 px, ou les intitules
 * complets rediges par Nawal tiennent sans probleme. */
export const libelleAffichable = (cle) => {
  const e = trouverCompetence(cle)
  if (!e) return cle
  return e.court || e.cle || cle
}

export const libelleComplet = (cle, langue) => {
  const e = trouverCompetence(cle)
  if (!e) return cle
  if (langue === "en") return e.libelleEn || e.libelle || e.cle || cle
  return e.libelle || e.cle || cle
}

/* Les deux autres champs redigés par Nawal, dans la langue demandée. On retombe
   sur le français si la traduction manque : mieux vaut une phrase en français
   qu'un trou dans le rapport. */
export const mesureDe = (entree, langue) =>
  (langue === "en" ? (entree?.mesureEn || entree?.mesure) : entree?.mesure) || ""

export const pertinenceDe = (entree, langue) =>
  (langue === "en" ? (entree?.pertinenceEn || entree?.pertinence) : entree?.pertinence) || ""

/* Les familles dans l'ordre voulu par Nawal, avec leurs compétences. */
export const parFamille = (langue) => Object.keys(FAMILLES)
  .sort((a, b) => FAMILLES[a].ordre - FAMILLES[b].ordre)
  .map((f) => ({ famille: f,
                 libelle: (langue === "en" ? (FAMILLES[f].libelleEn || FAMILLES[f].libelle) : FAMILLES[f].libelle),
                 competences: REFERENTIEL.filter((e) => e.famille === f) }))

export default REFERENTIEL
